import { X } from "lucide-react"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import { type ReactNode, useEffect, useId, useRef } from "react"
import { createPortal } from "react-dom"

/**
 * Diálogo modal.
 *
 * ⚠ **Modal significa que el resto NO existe mientras está abierto**, y eso son
 * cuatro cosas, no una capa oscura:
 *
 * - **Escape lo cierra, y sólo a él.** Es lo primero que prueba quien no encuentra
 *   el botón. Con un cajón detrás cerraba los dos: ver el oyente en captura.
 * - **El foco entra al abrirse y vuelve de donde vino al cerrarse.** Sin lo segundo,
 *   quien navega con teclado acaba al principio de la página y tiene que recorrerla
 *   entera para volver a donde estaba.
 * - **El tabulador da la vuelta dentro.** Si no, se escapa a los controles de
 *   detrás — que están tapados y siguen siendo pulsables.
 * - **`aria-modal` y `role="dialog"`**, para que un lector anuncie que lo de detrás
 *   dejó de estar disponible en vez de leerlo igual.
 *
 * ⚠ **El fondo cierra al pulsarlo, pero sólo el fondo.** Un clic que empieza dentro
 * y termina fuera —seleccionar texto y soltar el ratón pasado el borde— no cuenta:
 * se comprueba que el `mousedown` fue en el fondo, no sólo el clic.
 */

type Props = {
  abierto: boolean
  titulo: string
  descripcion?: string
  onCerrar: () => void
  children: ReactNode
  /** `sm` para confirmaciones; `md` es lo normal para un formulario. */
  ancho?: "sm" | "md" | "lg"
}

const ANCHO = { sm: "max-w-sm", md: "max-w-2xl", lg: "max-w-4xl" }

export function Modal({ abierto, titulo, descripcion, onCerrar, children, ancho = "md" }: Props) {
  const sinMovimiento = useReducedMotion()
  const caja = useRef<HTMLDivElement | null>(null)
  const origenDelFoco = useRef<HTMLElement | null>(null)
  const empezoEnElFondo = useRef(false)
  const idTitulo = useId()
  const idDescripcion = useId()

  /**
   * ⚠ **`onCerrar` en una referencia, y NO en las dependencias del efecto.**
   *
   * Todas las pantallas lo pasan en línea —`onCerrar={() => setAbierto(false)}`— que
   * es una identidad nueva en cada render. Teniéndolo como dependencia, cada tecla
   * escrita en un campo del modal re-ejecutaba el efecto, y su limpieza hace
   * `origenDelFoco.current?.focus()`: **el foco saltaba al botón que abrió el
   * modal**. Se escribía una letra y había que volver a pulsar el campo. Para cada
   * letra.
   *
   * Ninguna prueba lo vio porque todas tecleaban con un solo `fireEvent.change`, que
   * es un evento y no una secuencia; el fallo empieza en el segundo carácter.
   *
   * La referencia se actualiza en cada render —sin lista de dependencias— para que
   * Escape llame siempre a la función VIGENTE. Guardarla una vez sería el otro
   * defecto clásico: cerrar el modal invocando el `onCerrar` del primer render.
   */
  const alCerrar = useRef(onCerrar)
  useEffect(() => {
    alCerrar.current = onCerrar
  })

  useEffect(() => {
    if (!abierto) return
    origenDelFoco.current = document.activeElement as HTMLElement | null
    // Al cuadro, no al primer campo: un lector lee el título antes de que empiece a
    // teclearse, y quien ve la pantalla no se encuentra el cursor puesto sin saber
    // dónde.
    caja.current?.focus()

    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation()
        alCerrar.current()
        return
      }
      if (e.key !== "Tab" || caja.current === null) return
      const enfocables = caja.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )
      if (enfocables.length === 0) return
      const primero = enfocables[0]
      const ultimo = enfocables[enfocables.length - 1]
      if (primero === undefined || ultimo === undefined) return
      // La vuelta, en los dos sentidos.
      if (e.shiftKey && document.activeElement === primero) {
        e.preventDefault()
        ultimo.focus()
      } else if (!e.shiftKey && document.activeElement === ultimo) {
        e.preventDefault()
        primero.focus()
      }
    }

    // ⚠ **En fase de CAPTURA, y no es un detalle.** El cajón que puede haber detrás
    // —la ficha de una persona con sus contratos— escucha Escape en `document`
    // igual que esto. En burbuja los dos oyentes son del mismo destino, y ahí
    // `stopPropagation()` no detiene al otro; `stopImmediatePropagation()` tampoco
    // serviría, porque el cajón se abre ANTES y su oyente está registrado primero.
    // Una sola pulsación cerraba los dos y se perdía la ficha entera. En captura el
    // modal ve el evento antes de que baje al objetivo, así que lo corta sin
    // depender del orden de registro.
    document.addEventListener("keydown", alTeclear, true)
    // El fondo no se desplaza mientras hay un modal delante: si no, la rueda mueve
    // la página de detrás y se pierde el sitio donde se estaba.
    const desbordeAnterior = document.body.style.overflow
    document.body.style.overflow = "hidden"

    return () => {
      document.removeEventListener("keydown", alTeclear, true)
      document.body.style.overflow = desbordeAnterior
      origenDelFoco.current?.focus()
    }
  }, [abierto])

  // ⚠ **En un portal a `body`, como `Cajon` y `MenuMovil` (12/10/2026).** El área
  // de trabajo lleva `backdrop-filter`, y eso atrapa a todo descendiente `fixed`:
  // pintado dentro de ella, el fondo del modal medía lo que mide el área y NO
  // tapaba ni el menú ni el encabezado —que seguían pulsables con un diálogo que se
  // anuncia `aria-modal`—, el `overflow-hidden` del área lo recortaba y un cajón
  // abierto (en `body`, `z-40`) le quedaba ENCIMA. Los colores no cambian al salir:
  // el tema lo fija la raíz (`data-tema`), igual que la `.paleta-oscura` del área.
  return createPortal(
    <AnimatePresence>
      {abierto && (
        <motion.div
          // En el teléfono, 8 px de margen y arriba del todo: el 8vh y los 16 px del
          // monitor se comían media pantalla de un formulario que ya es largo. El
          // `env()` lo aparta del notch, porque el modal no hereda `zona-segura`.
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-2 pt-[max(0.75rem,env(safe-area-inset-top))] pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:p-4 sm:pt-[8vh]"
          style={{ backgroundColor: "rgb(2 6 23 / 0.55)", backdropFilter: "blur(2px)" }}
          initial={sinMovimiento ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: sinMovimiento ? 0 : 0.12 }}
          onMouseDown={(e) => {
            empezoEnElFondo.current = e.target === e.currentTarget
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget && empezoEnElFondo.current) onCerrar()
          }}
        >
          <motion.div
            ref={caja}
            role="dialog"
            aria-modal="true"
            aria-labelledby={idTitulo}
            aria-describedby={descripcion === undefined ? undefined : idDescripcion}
            tabIndex={-1}
            className={`panel-flotante w-full ${ANCHO[ancho]} outline-none`}
            style={{ boxShadow: "var(--elev-3), var(--realce)" }}
            initial={sinMovimiento ? false : { opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={sinMovimiento ? { opacity: 0 } : { opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: sinMovimiento ? 0 : 0.18, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="mb-3 flex items-start justify-between gap-4">
              <div>
                <h2 id={idTitulo} className="text-base font-semibold">
                  {titulo}
                </h2>
                {descripcion !== undefined && (
                  <p
                    id={idDescripcion}
                    className="mt-1 text-sm"
                    style={{ color: "var(--fg-muted)" }}
                  >
                    {descripcion}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={onCerrar}
                aria-label="Cerrar"
                // 44 px en el teléfono, que se pulsa con el pulgar; el tamaño de antes en
                // el monitor, donde se apunta con el ratón.
                className="boton-secundario flex h-11 w-11 shrink-0 items-center justify-center p-0 sm:h-auto sm:w-auto sm:px-2 sm:py-1"
              >
                <X size={16} aria-hidden={true} />
              </button>
            </div>
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  )
}
